import { ArrowUp, ShieldAlert, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CoachCard, CoachMessage } from '../../shared/api';
import { kgToLb, formatWeight } from '../../shared/units';
import { useCoachChat } from '../api/coach';
import { useClearCoach, useCoachMessages, useSettings } from '../api/hooks';
import { useAIAllowance } from '../lib/aiAllowance';
import { AI_OFF_FIX, useAIOffReason } from '../lib/aiOff';
import { kcal } from '../lib/format';
import { useUI } from '../lib/ui';
import { AIRequestsLeft } from './AIRequestsLeft';
import { BarChart, Sparkline } from './charts';
import { Markdown } from './Markdown';

const PROMPTS = [
  'How did my protein look this week?',
  'Why did my weight go up this week?',
  "What's a good dinner for what I have left today?",
  'Am I on track for my goal?',
];

function Card({ card }: { card: CoachCard }) {
  if (card.kind === 'weight') {
    const values = card.trend.map((t) => (card.unit === 'lb' ? kgToLb(t.kg) : t.kg));
    const last = card.trend[card.trend.length - 1];
    return (
      <div className="mini">
        <div className="mini__title">{card.title}</div>
        <Sparkline values={values} />
        {last && <div className="tiny muted">Trend now {formatWeight(last.kg, card.unit)}</div>}
      </div>
    );
  }
  if (card.kind === 'intake') {
    return (
      <div className="mini">
        <div className="mini__title">{card.title}</div>
        <BarChart days={card.days.slice(-14)} target={card.target} direction="target" unit={card.unit} height={90} label={card.title} compact />
      </div>
    );
  }
  return (
    <div className="mini">
      <div className="mini__title">{card.title}</div>
      <div className="small num">
        <b>{kcal(card.totals.kcal)}</b> kcal · {Math.round(card.totals.protein ?? 0)} g protein · {card.entryCount} items
      </div>
    </div>
  );
}

function Message({ msg }: { msg: CoachMessage }) {
  if (msg.role === 'user') return <div className="msg msg--user">{msg.text}</div>;
  return (
    <div className={`msg msg--ai ${msg.safety ? 'msg--safety' : ''}`}>
      {msg.safety && (
        <span className="eyebrow">
          <ShieldAlert size={13} /> Support
        </span>
      )}
      {msg.cards.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          {msg.cards.map((c, i) => (
            <Card key={i} card={c} />
          ))}
        </div>
      )}
      <div>
        <Markdown text={msg.text} />
      </div>
    </div>
  );
}

export function CoachPanel({ className = '' }: { className?: string }) {
  const { today, toast } = useUI();
  const settings = useSettings();
  const aiEnabled = settings.data?.ai.enabled ?? false;
  const offReason = useAIOffReason();
  // With no AI requests left, the suggested questions wait for the reset. The message box stays
  // open: the server answers crisis messages without the AI, before any limit is checked.
  const { exhausted } = useAIAllowance();
  const noteId = useId();
  const messages = useCoachMessages();
  const clear = useClearCoach();
  const { turn, send, busy, dismiss } = useCoachChat(today);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const list = useMemo(() => messages.data ?? [], [messages.data]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [list.length, turn?.text, turn?.cards.length, turn?.status]);

  const submit = (text: string) => {
    if (!text.trim() || busy) return;
    setDraft('');
    void send(text);
  };

  return (
    <div className={`coach ${className}`}>
      <div className="coach__head">
        <span className="coach__avatar" aria-hidden="true">
          <Sparkles size={18} />
        </span>
        <div className="grow">
          <div style={{ fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: 18 }}>Coach</div>
          <div className="tiny muted">Answers from your logs · not medical advice</div>
        </div>
        {list.length > 0 && (
          <button
            className="btn btn--quiet btn--icon btn--sm"
            aria-label="Clear conversation"
            onClick={() => clear.mutate(undefined, { onSuccess: () => toast('Conversation cleared') })}
          >
            <Trash2 size={17} />
          </button>
        )}
      </div>

      <div className="coach__list" ref={listRef} aria-live="polite">
        {list.length === 0 && !turn && (
          <div className="stack" style={{ gap: 14, marginTop: 'auto' }}>
            <div>
              <h3 style={{ fontSize: 22 }}>
                Ask about <span className="ai-text">your</span> data
              </h3>
              <p className="small muted" style={{ marginTop: 6 }}>
                Coach looks at your food log and weigh-ins before answering.
              </p>
            </div>
            <div className="prompts">
              {PROMPTS.map((p) => (
                <button key={p} className="prompt" disabled={!aiEnabled || exhausted} aria-describedby={exhausted ? noteId : undefined} onClick={() => submit(p)}>
                  {p}
                </button>
              ))}
            </div>
            {!aiEnabled && <p className="small muted">Coach needs AI. {AI_OFF_FIX[offReason]}</p>}
          </div>
        )}
        {list.map((m) => (
          <Message key={m.id} msg={m} />
        ))}
        {turn && (
          <>
            <div className="msg msg--user">{turn.user}</div>
            <div className="msg msg--ai">
              {turn.cards.map((c, i) => (
                <Card key={i} card={c} />
              ))}
              {turn.text && (
                <div className={turn.error ? '' : 'caret'}>
                  <Markdown text={turn.text} />
                </div>
              )}
              {turn.status && (
                <span className="thinking muted">
                  <span className="thinking__orb" aria-hidden="true" /> {turn.status}
                </span>
              )}
              {turn.error && (
                <div className="banner banner--warn">
                  <ShieldAlert size={17} />
                  <span className="small grow">{turn.error}</span>
                  <button className="btn btn--quiet btn--xs" onClick={dismiss}>
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="coach__foot">
        <AIRequestsLeft id={noteId} />
        <form
          className="coach__input"
          onSubmit={(e) => {
            e.preventDefault();
            submit(draft);
          }}
        >
          <textarea
            rows={1}
            value={draft}
            maxLength={2000}
            placeholder={aiEnabled ? 'Ask about your food or weight…' : 'Coach is off'}
            aria-label="Message Coach"
            disabled={!aiEnabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(draft);
              }
            }}
          />
          <button className="btn btn--primary btn--icon" aria-label="Send" disabled={!aiEnabled || busy || !draft.trim()}>
            <ArrowUp size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}
